import { useEffect, useRef, useState } from 'react';
import { useRange, type UseRangeProps } from 'react-instantsearch';
import { Slider } from '@fpsm/cxy-components/Slider';

const timeout = 200;

/**
 * `max` must stay constant for the life of the slider: changing it recreates the range widget, which
 * clears its refinement. Use `displayMax` for a cap that varies with other refinements.
 */
export const SliderFilter = ({
  displayMax,
  isDisabled = false,
  label,
  step = 1,
  ...props
}: UseRangeProps & { displayMax?: number; isDisabled?: boolean; label: string; step?: number }) => {
  const { canRefine, range, refine, start } = useRange(props);
  const min = range.min as number;
  const max = displayMax ?? range.max;
  const lowestValue = Number(start[0]);

  const [value, setValue] = useState(min);
  const timerId = useRef<NodeJS.Timeout>(undefined);
  const appliedMax = useRef(range.max);

  const from = Math.max(min, Number.isFinite(lowestValue) ? lowestValue : min);

  useEffect(() => {
    setValue(from);
  }, [from]);

  useEffect(() => {
    if (appliedMax.current === max) return;
    appliedMax.current = max;

    const upperValue = Number.isFinite(Number(start[1])) ? Number(start[1]) : range.max;
    if (max !== undefined && upperValue !== max) {
      refine([Number.isFinite(lowestValue) ? Math.min(lowestValue, max) : undefined, max]);
    }
  }, [lowestValue, max, range.max, refine, start]);

  return (
    <Slider
      disabled={isDisabled || !canRefine}
      label={label}
      min={range.min}
      onValueChange={(values: number[]) => {
        setValue(values[0]);

        clearTimeout(timerId.current);
        timerId.current = setTimeout(() => {
          refine([values[0], appliedMax.current]);
        }, timeout);
      }}
      step={step}
      unit="%"
      value={[value]}
      {...props}
      max={max}
    >
      EV
    </Slider>
  );
};
